import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  Checkbox,
  FormControl,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  SideSheetHeader,
  Text,
  TextField,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { CreateProjectInput, CreateProjectResult } from '@bechtel/ht-types';
import { Formik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { client } from '~/apollo-client';
import { Backdrop } from '~/components/backdrop';
import { CREATE_PROJECT } from '~/gql/projects';
import { NewProjectContainer } from './styled';
export interface NewProjectProps {
  onClose: () => void;
}
const ErrorField = ({ errorMsg }) => {
  return (
    <Box pt={{ sm: '200' }} pl={{ sm: '300' }}>
      <Text size="250" color="bechtel.red">
        {errorMsg}
      </Text>
    </Box>
  );
};
export const NewProject: FC<NewProjectProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState<boolean>(false);
  const validationSchema = object().shape({
    thProjectNumber: string().trim().required(t('TOOLHOUND_PROJECT_NUMBER_MANDATORY')),
    projectName: string().trim().required(t('PROJECT_NAME_MANDATORY')),
    projectNumber: string().trim().required(t('PROJECT_NUMBER_MANDATORY')),
  });
  const [createProjectQuery, { loading }] = useMutation<
    { createProject: CreateProjectResult },
    { input: CreateProjectInput }
  >(CREATE_PROJECT);
  const dispatchToast = useToastsDispatch();
  return (
    <NewProjectContainer>
      <SideSheetHeader title={t('NEW_PROJECT_TITLE')}>
        <SideSheetClose onClick={onClose} />
      </SideSheetHeader>

      <Formik
        initialValues={{
          thProjectNumber: '',
          projectName: '',
          projectNumber: '',
          subJobNumber: '',
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={async (values) => {
          const createProjectInput: CreateProjectInput = {
            isActive: true,
            thParentLocationId: values.thProjectNumber,
            subJobNumber: values.subJobNumber,
            jobNumber: values.projectNumber,
            name: values.projectName,
          };
          createProjectQuery({
            variables: {
              input: createProjectInput,
            },
            onCompleted: (data) => {
              if (data?.createProject.error) {
                const rolePermissionToast: ToastAction = {
                  type: 'ADD_TOAST',
                  payload: {
                    toastType: ToastType.DANGER,
                    content:
                      data?.createProject.error === 'FAILED_TO_CREATE_PROJECT'
                        ? t(data?.createProject.error)
                        : t('COMMON_ERROR_MESSAGE'),
                    autoDismissTimeout: 3000,
                  },
                };
                dispatchToast(rolePermissionToast);
                onClose();
              } else {
                setHasError(false);
                const newProjectToast: ToastAction = {
                  type: 'ADD_TOAST',
                  payload: {
                    toastType: ToastType.SUCCESS,
                    content: t('NEW_PROJECT_CONFIGURATION_SUCCESS'),
                    autoDismissTimeout: 3000,
                  },
                };
                dispatchToast(newProjectToast);
                onClose();
              }
              client.cache.evict({ fieldName: 'searchUserProjects' });
            },
            refetchQueries: ['SearchUserProjects'],
            onError: (errors) => {
              const errorProjectToast: ToastAction = {
                type: 'ADD_TOAST',
                payload: {
                  toastType: ToastType.DANGER,
                  content: t('NEW_PROJECT_ERROR_MSG'),
                  autoDismissTimeout: 3000,
                },
              };
              dispatchToast(errorProjectToast);
            },
          });
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid, touched, resetForm, errors }) => {
          return (
            <>
              {loading && <Backdrop />}
              <SideSheetBody>
                <Box css={{ width: '100%' }}>
                  <Box style={{ width: '100%' }} flexDirection={{ sm: 'column' }} p={{ sm: '500' }}>
                    <Box pb={{ sm: '700' }}>
                      <FormControl label={t('TOOLHOUND_PROJECT_LOCATION')} css={{ width: '100%' }}>
                        <TextField
                          name="thProjectNumber"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.thProjectNumber || ''}
                        />
                        <ErrorField errorMsg={errors.thProjectNumber} />
                      </FormControl>
                    </Box>
                    <Box pb={{ sm: '700' }}>
                      <FormControl label={t('PROJECT_NAME')} css={{ width: '100%' }}>
                        <TextField
                          name="projectName"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.projectName || ''}
                        />
                        <ErrorField errorMsg={errors.projectName} />
                      </FormControl>
                    </Box>
                    <Box pb={{ sm: '700' }}>
                      <FormControl label={t('PROJECT_NUMBER')} css={{ width: '100%' }}>
                        <TextField
                          name="projectNumber"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.projectNumber || ''}
                        />
                        <ErrorField errorMsg={errors.projectNumber} />
                      </FormControl>
                    </Box>
                    <Box pb={{ sm: '700' }}>
                      <FormControl label={t('SUB_JOB_NUMBER')} css={{ width: '100%' }}>
                        <TextField
                          name="subJobNumber"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.subJobNumber || ''}
                        />
                      </FormControl>
                    </Box>
                    <Box
                      pb={{ sm: '500' }}
                      gap={{ sm: '400' }}
                      style={{ borderBottom: '1px solid lightGrey' }}
                      flexDirection={{ sm: 'column' }}
                    >
                      {/* TODO:- Options to configure diffrent modules */}
                      <Text size="400">Modules To be Configured</Text>
                      <Text size="200" color="grey.500">
                        Note:- This option will be enabled soon. Currently, the tools are configured by default.
                      </Text>
                    </Box>
                    <Box pb={{ sm: '500' }} pt={{ sm: '500' }} gap={{ sm: '400' }}>
                      <Checkbox checked={true} title="Tools" disabled={true}></Checkbox>
                      <Text size="400">Tools</Text>
                    </Box>
                    <Box pb={{ sm: '700' }} gap={{ sm: '400' }}>
                      <Checkbox checked={false} title="Tools" disabled={true}></Checkbox>
                      <Text size="400">Equipment</Text>
                    </Box>
                  </Box>
                </Box>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }} gap={{ sm: '500' }}>
                  <Box>
                    <Button
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={() => {
                        onClose();
                        resetForm();
                      }}
                      disabled={false}
                    >
                      {t('Close')}
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      appearance={ButtonAppearance.PRIMARY}
                      onClick={() => {
                        handleSubmit();
                      }}
                      disabled={!dirty || !isValid}
                    >
                      {t('Submit')}
                    </Button>
                  </Box>
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
    </NewProjectContainer>
  );
};
